﻿<template>
    <q-tab-panel name="EventResultsTab">
        <q-tabs v-model="selClass" align="left" dense no-caps narrow-indicator active-color="secondary"
                v-if="moreClasses">
            <q-tab :ripple="false" :name="boatClass.boatClass.id" :label="boatClass.eventClassName"
                   v-for="boatClass in attendedClasses"
                   :key="boatClass.id" />
        </q-tabs>

        <div v-if="allowOnlyCaptainsOption || isHandicapUsed || isBestTimeUsed" class="row justify-between items-center filter-bar">
            <div>
                <q-btn-toggle
                    v-if="isBestTimeUsed"
                    v-model="resultsType"
                    flat stretch
                    toggle-color="secondary"
                    :options="[
                        { label: ts( 'raceResults' ), value: 'races' },
                        { label: ts( 'bestResultTime' ), value: 'bestTime' }
                    ]"
                />
            </div>
            <div class="text-right">
                <q-toggle
                    v-if="allowOnlyCaptainsOption"
                    v-model="showOnlyCaptains" color="secondary"
                    :label="showOnlyCaptains ? ts( 'showOnlyCaptains' ) : ts( 'showCrewOnBoats' )"
                    @update:model-value="saveShowOnlyCaptains" />
            </div>
        </div>

        <div class="q-pa-md" v-if="isCategoriesUsed">
            <q-select
                v-model="categoryDisplayed" :label="ts( 'categoryDisplayed' )" :options="categoryList"
                option-value="value" option-label="label" class="qe"
                emit-value map-options input-debounce="300" />
        </div>

        <q-markup-table flat class="q-mt-lg">
            <thead>
            <tr>
                <th class="text-right">{{ ts( 'orderShortcut' ) }}</th>
                <th class="text-left">{{ ts( 'sailNo' ) }}</th>
                <th class="text-left" v-if="useBoatNames && isBoatNamesUsed">{{ ts( 'boatName' ) }}</th>
                <th class="text-left">{{ ts( 'crew' ) }}</th>
                <th class="text-left" v-if="isCategoriesUsed && categoryDisplayed === null">{{ ts( 'category' ) }}</th>
                <th class="text-right" v-for="( race, index ) in availableRaces" :key="race.id">R{{ index + 1 }}&puncsp;&ensp;</th>
                <th class="text-right">{{ ts( 'total' ) }}</th>
                <th class="text-right" v-if="categoryDisplayed === null">{{ ts( 'logPoints' ) }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-if="( results?.length ?? 0 ) === 0 || ( availableRaces?.length ?? 0 ) === 0">
                <td colspan="6" class="text-center">
                    <div class="q-mt-md">
                        <q-icon name="warning" color="negative" size="40px" />
                        <span class="q-ml-md">{{ ts( 'noEnteredResults' ) }}</span>
                    </div>
                </td>
            </tr>
            <tr v-for="( boat ) in results" :key="boat.id" v-else>
                <td class="text-right">{{ boat.index }}.</td>
                <td>
                    <span :class="'fi ' + 'fi-' + boat.boat.sailNoFlag" />
                    {{ boat.boat.sailNo }}
                </td>
                <td v-if="useBoatNames && isBoatNamesUsed">{{ boat.boat.name }}</td>
                <td class="minicrewlist">
                    <div v-if="showOnlyCaptains">
                        {{ boat.boat.orderedCrewList.length > 0 ? boat.boat.orderedCrewList[ 0 ].reverseFullName : '' }}
                    </div>
                    <div v-for="crew in boat.boat.orderedCrewList" :key="crew.id" :class="crew.captain ? 'captain' : ''"
                         v-else>
                        <q-icon
                            :name="crew.sex === 'male' ? 'man' : 'woman'"
                            :color="crew.sex === 'male' ? 'blue' : 'red'"
                            size="1.5em" />
                        <span :title="crew.regId + ( wsIdRequired ? ' / ' + crew.wsId : '' ) + ' &ndash; ' + crew.visibleClub">{{ crew.reverseFullName }}</span>
                    </div>
                </td>
                <td class="minicrewlist" v-if="isCategoriesUsed && categoryDisplayed === null">
                    <div v-for="category in boat.boat.orderedCategories" :key="category.id">
                        {{ category.visibleShortcut }}
                    </div>
                </td>
                <td class="text-right" v-for="race in boat.results" :key="race.race"
                    :class="{ 'discarded': race.isDiscarded, 'auto_result': ! race.decided }"
                    :title="resultsType === 'bestTime' ? ( tcs( 'pointsNo', race.points ) + ( race.validFinish ? ' &nbsp; (' + race.result + ')' : '' ) ) : ( tcs( 'pointsNo', race.points ) + ( race.details?.isHandicap ? ' &nbsp; (' + ds( race.details?.finishTime, DateFormat.Time ) + ' &rarr; ' + race.details?.realTime() + ' s &times; ' + ( Math.round( ( boat.boat.calcHandicapCoef( null, race.details?.eventRaceId ) + Number.EPSILON ) * 10000 ) / 10000 ) + ' = ' + ( Math.round( ( race.details?.calculatedTime() + Number.EPSILON ) * 100 ) / 100 ) + ' s)' : '' ) )">
                    {{ isPositiveNumber( race.result ) && ( 10 * race.result ) % 10 !== 0 ? ns( race.result, 'points' ) : race.result
                    }}{{ race.isDiscarded ? '*' : '' }}{{ isPositiveNumber( race.result ) && ( 10 * race.result ) % 10 !== 0 ? '' : '&puncsp;&ensp;' }}
                </td>
                <td class="text-right text-weight-bold">{{ resultsType === 'bestTime' && boat.pointsText ? boat.pointsText : ns( boat.points, 'points' ) }}</td>
                <td class="text-right" v-if="categoryDisplayed === null">{{ ns( boat.logPoints, 'logPoints' ) }}</td>
            </tr>
            </tbody>
        </q-markup-table>

        <div class="text-right q-mt-lg" v-if="( results?.length ?? 0 ) > 0 && ( availableRaces?.length ?? 0 ) > 0">
            <q-btn :label="ts( categoryDisplayed === null ? 'printSemiResults' : 'printCategorySemiResults' )" outline
                   color="secondary" @click="printResults( false, true )"
                   v-if="allowOnlyCaptainsOption && showOnlyCaptains" />
            <q-btn :label="ts( categoryDisplayed === null ? 'printSemiResults' : 'printCategorySemiResults' )" outline
                   color="secondary" @click="printResults( false )"
                   v-else-if="!allowOnlyCaptainsOption" />
            <q-btn-dropdown
                outline color="secondary" :label="ts( 'printSemiResults' )" v-else>
                <q-list>
                    <q-item clickable v-close-popup @click="printResults( false, false )">
                        <q-item-section>
                            {{ ts( 'withCrew' ) }}
                        </q-item-section>
                    </q-item>
                    <q-item clickable v-close-popup @click="printResults( false, true )">
                        <q-item-section>
                            {{ ts( 'onlyCaptains' ) }}
                        </q-item-section>
                    </q-item>
                </q-list>
            </q-btn-dropdown>

            <q-btn :label="ts( categoryDisplayed === null ? 'printResults' : 'printCategoryResults' )" outline
                   color="secondary" class="q-ml-md"
                   @click="printResults( true, true )" v-if="allowOnlyCaptainsOption && showOnlyCaptains" />
            <q-btn :label="ts( categoryDisplayed === null ? 'printResults' : 'printCategoryResults' )" outline
                   color="secondary" class="q-ml-md" @click="printResults( true )"
                   v-else-if="!allowOnlyCaptainsOption" />
            <q-btn-dropdown class="q-ml-md"
                            outline color="secondary" :label="ts( 'printResults' )" v-else>
                <q-list>
                    <q-item clickable v-close-popup @click="printResults( true, false )">
                        <q-item-section>
                            {{ ts( 'withCrew' ) }}
                        </q-item-section>
                    </q-item>
                    <q-item clickable v-close-popup @click="printResults( true, true )">
                        <q-item-section>
                            {{ ts( 'onlyCaptains' ) }}
                        </q-item-section>
                    </q-item>
                </q-list>
            </q-btn-dropdown>
        </div>
    </q-tab-panel>
</template>

<script setup lang="ts">
import { individualLegendDef, otherResults } from '@/orm/catalogs/OtherResults'
import { localization } from '@/plugins/i18n'
import { DateFormat, ds, ns, tcs, ts } from '@/plugins/i18nFormat'
import nvl from '@/utils/nvl'
import { Options } from '@model/Setting'
import EventsRepo from '@repo/EventsRepo'
import SettingsRepo from '@repo/SettingsRepo'
import { isPositiveNumber } from "is-what"
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { useRepo } from 'pinia-orm'
import { QMarkupTable, QSelect, QTab, QTabPanel, QTabs } from 'quasar'
import { sprintf } from 'sprintf-js'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { buildPrintMeta, buildStandardHeader } from '@/plugins/printReports'

pdfMake.addVirtualFileSystem( pdfFonts )

const route = useRoute()

const props = defineProps<{ event: any }>()

const event = computed( () => ( props.event ?? useRepo( EventsRepo ).getEvent( String( route.params.eventId ), true ) ) )

const attendedClasses = computed( () => ( event.value?.orderedClasses ) )
const moreClasses = computed( () => ( attendedClasses.value.length > 1 ) )
const selClass = ref( attendedClasses.value[ 0 ].boatClass.id )

const availableRaces = computed( () => ( event.value.availableRaces( selClass.value, true ) ) )

const results = computed( () => ( event.value.results( selClass.value, categoryDisplayed.value, true, availableRaces.value, resultsType.value ) ) )

const useBoatNames = computed( () => ( event.value.getClass( selClass.value )?.boatClass.useBoatNames ) )

const maxCrewOnClass = computed( () => ( event.value.getClass( selClass.value )?.maxCrew ?? 0 ) )
const isBoatNamesUsed = computed( () => ( event.value.getClass( selClass.value )?.isBoatNamesUsed ?? false ) )
const allowOnlyCaptainsOption = computed( () => ( maxCrewOnClass.value >= 3 ) )
const resultsType = ref( 'races' )
const showOnlyCaptains = ref( event.value.showOnlyCaptains )
const isHandicapUsed = computed( () => ( event.value.getClass( selClass.value )?.handicapSystem !== null ) )
const isBestTimeUsed = computed( () => ( event.value.getClass( selClass.value )?.bestTimeResults ) )
const isCategoriesUsed = computed( () => ( results.value.some( result => result.boat.categories.length > 0 ) ) )

const categoryDisplayed = ref( null )
const categoryList = computed( () => ( [
    { value: null, label: ts( 'categoryFull-all' ) },
    ...event.value.getClass( selClass.value ).orderedCategories.map( category => ( {
        value: category.id,
        label: category.visibleTitle,
        filename: category.fileName
    } ) )
] ) )

const wsIdRequired = computed( () => ( event.value.wsEventId !== null && event.value.wsEventId.length > 0 ) )

function saveShowOnlyCaptains ( value ) {
    useRepo( EventsRepo ).save( { id: event.value.id, showOnlyCaptains: value } )
}

async function printResults ( final = false, onlyCaptains = false ) {
    const reportLocale = event.value.forceEnglishReports ? 'en' : localization.global.locale.value

    const eventClass = event.value.getClass( selClass.value )
    const useDesc = event.value.descInResultsTitle !== null && event.value.descInResultsTitle !== undefined && event.value.descInResultsTitle.length !== 0
    const useCrewDesc = event.value.crewDescInResultsTitle !== null && event.value.crewDescInResultsTitle !== undefined && event.value.crewDescInResultsTitle.length !== 0
    const initialColNum = ( ( eventClass.boatClass.useBoatNames || useDesc || useCrewDesc || wsIdRequired.value ) ? 2 : 4 )
    const landscape = availableRaces.value.length >= initialColNum
    const inLineResults = initialColNum + ( landscape ? 3 + ( final ? 0 : 1 ) + ( isCategoriesUsed.value ? 0 : 1 ) : 1 )

    const now = ds( new Date(), DateFormat.DateTimeHuman, reportLocale )

    const otherDetails = results.value
        .map( ( boat ) => boat.results
            .filter( ( race ) => typeof race.result !== 'number' )
            .map( ( race ) => [ race.result, race.points, race.race, boat.boat.sailNo, race.details?.attr ?? 0, boat.boat.sailNoSortable, race.details?.orderInclHandicap ?? 0 ] )
        )
        .reduce( ( accumulator, current ) => accumulator.concat( current ), [] )

    const organizedDetails = new Map(
        Map.groupBy( otherDetails, ( res ) => res[ 0 ] )
            .entries().toArray()
            .map( ( [ code, records ] ) => [ code, Map.groupBy( records, ( res ) => res[ 1 ] ) ] )
    )

    const legend = otherResults().filter( ( other ) => organizedDetails.has( other.value ) )
        .map( ( l ) => ( {
            value: l.value, short: l.short, details: organizedDetails.get( l.value ),
            points: organizedDetails.get( l.value ).size === 1 ? organizedDetails.get( l.value ).entries().toArray()[ 0 ][ 0 ] : null
        } ) )

    const legendColumns = landscape ? 4 : 3
    const legendLength = []

    const individualLegend = otherDetails.filter( ( det ) => Object.hasOwn( individualLegendDef, det[ 0 ] ) )
        .sort( ( a, b ) => a[ 5 ].localeCompare( b[ 5 ] ) )
        .map( ( det ) => ( {
            'otherCode': det[ 0 ],
            'sailNo': det[ 3 ],
            'race': det[ 2 ],
            'formula': individualLegendDef[ det[ 0 ] ]
                .replace( 'base', isPositiveNumber( det[ 6 ] ) ? det[ 6 ] : 0 )
                .replace( 'total', tcs( 'pointsNoShort', det[ 1 ], { named: { n: ns( det[ 1 ], 'points', reportLocale ) }, locale: reportLocale } ) )
                .replace( 'attr %', ( isPositiveNumber( det[ 4 ] ) ? det[ 4 ] * 100.0 : 0 ) + ' %' )
                .replace( 'attr', isPositiveNumber( det[ 4 ] ) ? det[ 4 ] : 0 )
        } ) )

    let placed = 0

    for ( let leftColumns = legendColumns; leftColumns >= 1; leftColumns-- ) {
        legendLength.push( placed )

        placed += Math.ceil( ( legend.length - placed ) / leftColumns )
    }

    let pricetables = []

    if ( isCategoriesUsed.value && categoryDisplayed.value === null ) {
        pricetables = categoryList.value.filter( cat => cat.value !== null ).map( category => ( {
            'title': category.label, 'results': event.value.results( selClass.value, category.value, true ).slice( 0, 3 )
        } ) )
    }

    const document = await buildPrintMeta( {
        event: event.value,
        title: ts( final ? 'final-results' : 'intermediate-results', { timestamp: now }, { locale: reportLocale } ),
        pageOrientation: landscape ? 'landscape' : 'portrait',
        locale: reportLocale
    } )

    const content = {
        ...document,
        content: [
            buildStandardHeader( event.value, { locale: reportLocale } ),
            {
                marginTop: 8,
                marginBottom: 8,
                table: {
                    headerRows: 0,
                    widths: [ '*', 'auto', ...( ( categoryDisplayed.value === null && isCategoriesUsed.value ) ? [ 'auto', 'auto' ] : [] ) ],
                    body: [
                        [
                            {
                                marginTop: 4,
                                marginBottom: 4,
                                marginLeft: 4,
                                text: [
                                    { text: ts( 'boatClassShortcut', {}, { locale: reportLocale } ) + ':  ', style: 'em' },
                                    {
                                        text: eventClass.eventClassName,
                                        style: 'em'
                                    }
                                ]
                            },
                            {
                                marginTop: 4,
                                marginBottom: 4,
                                marginLeft: 8,
                                marginRight: 8,
                                alignment: 'right',
                                text: [
                                    { text: ts( 'noOfBoats', {}, { locale: reportLocale } ) + ':  ', style: 'em' },
                                    { text: results.value.length, style: 'em' }
                                ]
                            },
                            ...( ( categoryDisplayed.value === null && isCategoriesUsed.value ) ? [
                                {
                                    marginTop: 4,
                                    marginBottom: 4,
                                    marginLeft: 8,
                                    marginRight: 8,
                                    alignment: 'right',
                                    text: [
                                        { text: ts( 'plannedCoefficient', {}, { locale: reportLocale } ) + ':  ', style: 'em' },
                                        { text: eventClass.weight, style: 'em' }
                                    ]
                                },
                                {
                                    marginTop: 4,
                                    marginBottom: 4,
                                    marginRight: 4,
                                    alignment: 'right',
                                    text: [
                                        { text: ts( 'coefficient', {}, { locale: reportLocale } ) + ':  ', style: 'em' },
                                        { text: eventClass.calculatedWeight, style: 'em' }
                                    ]
                                }
                            ] : [] )
                        ],
                        ...( ( categoryDisplayed.value !== null && isCategoriesUsed.value ) ? [ [
                            {
                                colSpan: 2,
                                marginBottom: 4,
                                marginLeft: 4,
                                text: [
                                    { text: ts( 'category', {}, { locale: reportLocale } ) + ':  ', style: 'em' },
                                    {
                                        text: categoryList.value.find( c => c.value === categoryDisplayed.value )?.label ?? '',
                                        style: 'em'
                                    }
                                ]
                            }
                        ]
                        ] : [] )
                    ]
                },
                layout: {
                    vLineWidth: ( i, node ) => ( ( i === 0 || i === node.table.widths.length ) ? 1 : 0 ),
                    hLineWidth: ( i, node ) => ( ( i === 0 || i === node.table.body.length ) ? 1 : 0 ),
                    hLineColor: 'black',
                    vLineColor: 'black'
                }
            },
            ...( renderResults( eventClass, inLineResults, useDesc, useCrewDesc, landscape, final, onlyCaptains, results.value, false, true, reportLocale ) ),
            {
                marginTop: 12,
                columnGap: 16,
                columns: [
                    ...Array.range( 0, legendColumns ).map( i => ( {
                        width: '*',
                        stack: legend.slice( legendLength[ i ], i === 3 ? legend.length + 1 : legendLength[ i + 1 ] )
                            .map( result => result.value + ' – ' + result.short + ( result.points !== null ? ' ('
                                + tcs( 'pointsNoShort', result.points, { named: { n: ns( result.points, 'points', reportLocale ) } }, reportLocale ) + ')' : '' ) )
                    } ) )
                ],
                style: 'legend'
            },
            ...( individualLegend.length > 0 ? [ {
                headerRows: 1,
                marginTop: 12,
                table: {
                    widths: [ 'auto', 'auto', 'auto', '*' ],
                    body: [
                        [
                            { text: ts( 'result' ), style: 'em' },
                            { text: ts( 'sailNo' ), style: 'em' },
                            { text: ts( 'race' ), style: 'em' },
                            { text: ts( 'calc' ), style: 'em' }
                        ],
                        ...individualLegend.map( det => ( [ det.otherCode, det.sailNo, det.race, det.formula ] ) )
                    ]
                },
                layout: {
                    vLineWidth: () => ( 0 ),
                    hLineWidth: () => ( 0 )
                },
                style: 'legend'
            } ] : [] ),
            ...( ( eventClass.orderedCategories.length > 0 && categoryDisplayed.value === null ) ? [
                {
                    headerRows: 1,
                    marginTop: 12,
                    table: {
                        widths: [ 'auto', 'auto', '*' ],
                        body: [
                            [
                                { text: ts( 'shortcut' ), style: 'em' },
                                { text: ts( 'category' ), style: 'em' },
                                { text: ts( 'conditions' ), style: 'em' }
                            ],
                            ...eventClass.orderedCategories.map( category => ( [ category.visibleShortcut, category.visibleTitle, category.explanation ] ) )
                        ]
                    },
                    layout: {
                        vLineWidth: () => ( 0 ),
                        hLineWidth: () => ( 0 )
                    },
                    style: 'legend'
                }
            ] : [] ),
            ...( pricetables.map( ( cat ) => (
                [
                    {
                        marginTop: 12,
                        marginBottom: 4,
                        text: cat.title, style: 'em'
                    },
                    ...( renderResults( eventClass, inLineResults, useDesc, useCrewDesc, landscape, false, onlyCaptains, cat.results, true, false, reportLocale ) )
                ]
            ) ) ),
            {
                marginTop: 8,
                text: ts( final ? 'finalResultsInfo' : 'tempResultsInfo', {}, {}, reportLocale ),
                style: 'legend'
            }
        ]
    }

    pdfMake.createPdf( content ).download( 'ctl-' + event.value.ctlId + '-' + ( final ? 'results' : 'provisional-results' ) + '-'
        + eventClass.boatClass.shortcut.toLowerCase() + ( categoryDisplayed.value !== null ? '-' + ( categoryList.value.find( c => c.value === categoryDisplayed.value )?.filename ?? '' ) : '' )
        + '.pdf' )
}

function renderResults ( eventClass, inLineResults, useDesc, useCrewDesc, landscape, final, onlyCaptains, usedResults, ignoreCategories, showRaces, locale ) {
    const genHeader = availableRaces.value.map( ( race, index ) => ( {
        text: ( index + 1 ) + '.',
        style: 'em_race_result'
    } ) )

    const innerHeader = showRaces ? Array.range( 0, Math.ceil( genHeader.length / inLineResults ) ).map( i => genHeader.slice( i * inLineResults, ( i + 1 ) * inLineResults ) ) : [ '' ]

    if ( innerHeader.length > 1 && showRaces )
        while ( innerHeader[ innerHeader.length - 1 ].length < inLineResults )
            innerHeader[ innerHeader.length - 1 ].push( '' )

    return [
        {
            table: {
                headerRows: showRaces ? Math.ceil( availableRaces.value.length / inLineResults ) : 1,
                widths: [ 'auto', ( showRaces && inLineResults < 8 ) ? '*' : 'auto', showRaces ? '*' : 'auto',
                    ...( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? [ showRaces ? '*' : 'auto' ] : [] ),
                    ...( useDesc ? [ showRaces ? '*' : 'auto' ] : [] ),
                    'auto',
                    ...( wsIdRequired.value ? [ ( showRaces && !useCrewDesc ) ? '*' : 'auto' ] : [] ),
                    ...( useCrewDesc ? [ showRaces ? '*' : 'auto' ] : [] ),
                    ...( ( categoryDisplayed.value === null && isCategoriesUsed.value && !ignoreCategories ) ? [ 'auto' ] : [] ),
                    'auto', 'auto', showRaces ? '*' : 'auto', ...( showRaces ? innerHeader[ 0 ].map( () => landscape ? 26 : 24 ) : [] ), 'auto',
                    ...( ( final && ( !isCategoriesUsed.value || categoryDisplayed.value === null ) ) ? [ 'auto' ] : [] )
                ],
                body: [
                    ...innerHeader.map( ( inner, index ) => (
                        [
                            ...( index === innerHeader.length - 1 ? [
                                { text: ts( 'orderShortcut', {}, {}, locale ).toLowerCase(), style: 'em' },
                                { text: ts( 'sail', {}, {}, locale ).toLowerCase(), style: 'em' },
                                ...( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? [ { text: ts( 'boat', {}, {}, locale ).toLowerCase(), style: 'em' } ] : [] ),
                                ...( useDesc ? [ { text: event.value.descInResultsTitle, style: 'em' } ] : [] ),
                                { text: ts( 'crew', {}, {}, locale ).toLowerCase(), style: 'em' },
                                { text: ts( 'regId', {}, {}, locale ).toLowerCase(), style: 'em' },
                                ...( wsIdRequired.value ? [ { text: ts( 'wsId', {}, {}, locale ).toLowerCase(), style: 'em' } ] : [] ),
                                ...( useCrewDesc ? [ { text: event.value.crewDescInResultsTitle, style: 'em' } ] : [] ),
                                ...( ( categoryDisplayed.value === null && isCategoriesUsed.value && !ignoreCategories ) ? [ {
                                    text: ts( 'categoryShortcut', {}, {}, locale ).toLowerCase(),
                                    style: 'em'
                                }
                                ] : [] ),
                                { text: ts( 'bornShortcut', {}, {}, locale ).toLowerCase(), style: 'em' },
                                { text: ts( 'sexShort', {}, {}, locale ).toLowerCase(), style: 'em' },
                                { text: ts( 'club', {}, {}, locale ).toLowerCaseFirst(), style: 'em' }
                            ] : [ '', '', ...( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? [ '' ] : [] ), ...( useDesc ? [ '' ] : [] ), '', '',
                                ...( wsIdRequired.value ? [ '' ] : [] ), ...( useCrewDesc ? [ '' ] : [] ),
                                ...( ( categoryDisplayed.value === null && isCategoriesUsed.value && !ignoreCategories ) ? [ '' ] : [] ), '', '', '' ] ),
                            ...( showRaces ? inner : [] ),
                            ...( index === innerHeader.length - 1 ? [
                                { text: ts( 'points', {}, {}, locale ).toLowerCase(), style: 'em', alignment: 'right' },
                                ...( ( final && ( categoryDisplayed.value === null || !isCategoriesUsed.value ) ) ? [ {
                                    text: ts( 'logPointsPrint', {}, {}, locale ).toLowerCase(),
                                    style: 'em',
                                    alignment: 'right'
                                }
                                ] : [] )
                            ] : [ '', ...( ( final && ( categoryDisplayed.value === null || !isCategoriesUsed.value ) ) ? [ '' ] : [] ) ] )
                        ]
                    ) ),
                    ...( usedResults.map(
                        function ( boat ) {
                            const res = boat.results.map( ( race ) => ( {
                                text: ( ( isPositiveNumber( race.result ) && ( 10 * race.result ) % 10 !== 0 ) ? ns( race.result, 'points', locale ) : race.result ) + ( race.isDiscarded ? '*' : '' ),
                                style: race.decided ? '' : 'auto_result'
                            } ) )

                            let template = [
                                { text: sprintf( '%d.', boat.index ), alignment: 'right' },
                                boat.boat.sailNo,
                                ...( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? [ {
                                    text: nvl( boat.boat?.name, '' ).shorten( landscape ? 30 : 20 ),
                                    style: 'compact'
                                }
                                ] : [] ),
                                ...( useDesc ? [ {
                                    text: nvl( boat.boat?.detail, '' ).shorten( landscape ? 30 : 20 ),
                                    style: 'compact'
                                } ] : [] ),
                                '',
                                '',
                                ...( wsIdRequired.value ? [ '' ] : [] ),
                                ...( useCrewDesc ? [ '' ] : [] ),
                                ...( ( categoryDisplayed.value === null && isCategoriesUsed.value && !ignoreCategories )
                                    ? [ {
                                        text: boat.boat.orderedCategories.map( category => category.visibleShortcut ).join( ', ' ),
                                        style: landscape ? '' : 'compact'
                                    }
                                    ] : [] ),
                                '',
                                '',
                                '',
                                ...( showRaces ? res.slice( 0, innerHeader[ 0 ].length ) : [] ),
                                { text: ( resultsType.value === 'bestTime' && boat.pointsText ) ? boat.pointsText : ns( boat.points, 'points', locale ), alignment: 'right', style: 'em' },
                                ...( ( final && ( categoryDisplayed.value === null || !isCategoriesUsed.value ) ) ? [ {
                                    text: ns( boat.logPoints, 'logPoints', locale ),
                                    alignment: 'right',
                                    style: landscape ? '' : 'compact'
                                }
                                ] : [] )
                            ]

                            if ( boat.boat.crew === undefined || boat.boat.crew === null || boat.boat.crew.length === 0 ) {
                                return innerHeader.map( ( ign, index ) => {
                                    let line = template

                                    const rowRes = res.slice( ( index + 1 ) * inLineResults, ( index + 2 ) * inLineResults )

                                    if ( showRaces )
                                        while ( rowRes.length < inLineResults )
                                            rowRes.push( '' )

                                    template = [ '', '',
                                        ...( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? [ '' ] : [] ),
                                        ...( useDesc ? [ '' ] : [] ),
                                        '', '',
                                        ...( wsIdRequired.value ? [ '' ] : [] ),
                                        ...( useCrewDesc ? [ '' ] : [] ),
                                        ...( ( categoryDisplayed.value === null && isCategoriesUsed.value && !ignoreCategories ) ? [ '' ] : [] ),
                                        '', '', '', ...( showRaces ? rowRes : [] ), '',
                                        ...( ( final && ( categoryDisplayed.value === null || !isCategoriesUsed.value ) ) ? [ '' ] : [] )
                                    ]

                                    return line
                                } )
                            }

                            let limitedCrew = boat.boat.crew

                            if ( onlyCaptains )
                                limitedCrew = limitedCrew.filter( c => c.captain )

                            let crewRes = limitedCrew.map( function ( crew, index ) {
                                let line = template

                                const rowRes = res.slice( ( index + 1 ) * inLineResults, ( index + 2 ) * inLineResults )

                                if ( showRaces )
                                    while ( rowRes.length < innerHeader[ 0 ].length )
                                        rowRes.push( '' )

                                template = [ '', '',
                                    ...( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? [ '' ] : [] ),
                                    ...( useDesc ? [ '' ] : [] ),
                                    '', '',
                                    ...( wsIdRequired.value ? [ '' ] : [] ),
                                    ...( useCrewDesc ? [ '' ] : [] ),
                                    ...( ( categoryDisplayed.value === null && isCategoriesUsed.value && !ignoreCategories ) ? [ '' ] : [] ),
                                    '', '', '', ...( showRaces ? rowRes : [] ), '',
                                    ...( ( final && ( categoryDisplayed.value === null || !isCategoriesUsed.value ) ) ? [ '' ] : [] )
                                ]

                                let baseOffset = 3

                                if ( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value )
                                    baseOffset++

                                if ( useDesc )
                                    baseOffset++

                                if ( wsIdRequired )
                                    baseOffset++

                                if ( useCrewDesc )
                                    baseOffset++

                                if ( categoryDisplayed.value === null && isCategoriesUsed.value && !ignoreCategories )
                                    baseOffset++

                                let otherBaseOffset = 2

                                if ( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value )
                                    otherBaseOffset++

                                if ( useDesc )
                                    otherBaseOffset++

                                line[ otherBaseOffset ] = crew.reverseFullName?.shorten( 25 ) ?? ''
                                line[ otherBaseOffset + 1 ] = crew.regId

                                let myOffset = otherBaseOffset + 2

                                if ( wsIdRequired.value )
                                    line[ myOffset++ ] = crew.wsId

                                if ( useCrewDesc )
                                    line[ myOffset ] = crew.note

                                line[ baseOffset ] = crew.birthYear === undefined
                                    ? ''
                                    : {
                                        text: [
                                            { text: crew.birthYear },
                                            ...( crew.agedBirthYear != null ? [ { text: '*', sup: true } ] : [] )
                                        ]
                                    }
                                line[ baseOffset + 1 ] = crew.sex === 'male' ? ts( 'maleShortcut' ) : ts( 'femaleShortcut' )
                                line[ baseOffset + 2 ] = {
                                    text: crew.visibleClub?.shorten( landscape ? 30 : 20 ) ?? '',
                                    style: landscape ? '' : 'compact'
                                }

                                return line
                            } )

                            const solved = boat.boat.crew.length

                            crewRes = [
                                ...crewRes,
                                ...( showRaces ? Array.range( solved + 1, innerHeader.length + 1 ).map( ( ign, index ) => {
                                    let line = template

                                    const rowRes = res.slice( ( index + solved + 1 ) * inLineResults, ( index + solved + 2 ) * inLineResults )

                                    while ( rowRes.length < innerHeader[ 0 ].length )
                                        rowRes.push( '' )

                                    template = [ '', '',
                                        ...( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? [ '' ] : [] ),
                                        ...( useDesc ? [ '' ] : [] ),
                                        '', '',
                                        ...( wsIdRequired.value ? [ '' ] : [] ),
                                        ...( useCrewDesc ? [ '' ] : [] ),
                                        ...( ( categoryDisplayed.value === null && isCategoriesUsed.value && !ignoreCategories ) ? [ '' ] : [] ),
                                        '', '', '', ...( showRaces ? rowRes : [] ), '',
                                        ...( ( final && ( categoryDisplayed.value === null || !isCategoriesUsed.value ) ) ? [ '' ] : [] )
                                    ]

                                    return line
                                } ) : [] )
                            ]

                            return crewRes
                        }
                    ).flat( 1 ) )
                ]
            },
            layout: {
                vLineWidth: () => ( 0 ),
                hLineWidth: ( i, node ) => ( ( i === node.table.headerRows || i === node.table.body.length ) ? 1 : 0 ),
                vLineColor: 'black'
            },
            style: 'results'
        }
    ]
}
</script>

<style scoped lang="sass">
.minicrewlist
  font-size: 80% !important

.auto_result
  font-style: italic

.captain
  font-weight: bold

.filter-bar
  font-size: 80%
</style>
